import React from 'react';

// Assets
import emptyBenchmarksImage from '@/images/design-system-v2/empty-state/emptyBenchmarks.png';

// Components
import { Button } from '@capintel/component-lib/button';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import PortfoliosTableV2 from '@/views/components/tables/PortfoliosTableV2';
import { LoadingSpinner } from '@/views/components/common';
import { OwnProps as PortfolioLinePerformanceProps } from '@/views/components/misc/PortfolioLinePerformance';
import { PortfolioLinePerformance } from '@/views/components/misc';
import { PortfoliosTable } from '@/views/components/tables';
import { SearchInput } from '@/views/components/inputs';
import { Typography } from '@capintel/component-lib/typography';

// Legacy Component
import LegacyBenchmarkPortfolios from '@/views/containers/models/LegacyAllBenchmarkPortfolios';

// Enums
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '@/enums/Portfolio';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Libraries
import classNames from 'classnames';
import { SortingRule } from 'react-table';

// Types
import User from '@/types/user';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

// Utils
import { benchmarkToComparisonItem } from '@/utils/comparison';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { userHasPermissionEnabled, PERMISSIONS } from '@/utils/permissions';

type Props = {
  user: User;
  query?: string;
  portfoliosTableRef?: React.RefObject<PortfoliosTable<BenchmarkPortfolio>>;
  searching: boolean;
  initialized: boolean;
  benchmarkPortfolios: BenchmarkPortfolio[];
  evaluatingPortfolioForComparison: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onSearch: (query: string) => void;
  onDeletePortfolio: (benchmarkPortfolios: BenchmarkPortfolio[]) => void;
  onEditPortfolio: (benchmarkPortfolio: BenchmarkPortfolio) => void;
  onCompare: (portfobenchmarkPortfoliolio: BenchmarkPortfolio) => void;
  onCompareSelected: (benchmarkPortfolios: BenchmarkPortfolio[]) => void;
  onCreatePortfolio: () => void;
  comparingBenchmarkPortfolioIds: string[];
};

type LegacyProps = {
  onSort: (sorting: SortingRule[]) => void;
};

type WrapperProps = LegacyProps & Props;

class AllBenchmarkPortfoliosV2 extends React.Component<WrapperProps> {
  containerRef = React.createRef<HTMLDivElement>();

  private i18nEmptyStatePath = 'EmptyStateCard.Benchmark';

  getDisplayedColumns = () => {
    const { user } = this.props;
    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.COMPARE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE,
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  renderPortfoliosSection = (title: string, benchmarkPortfolios: BenchmarkPortfolio[]) => {
    const {
      comparingBenchmarkPortfolioIds,
      hasMore,
      initialized,
      onCompareSelected,
      onCreatePortfolio,
      onDeletePortfolio,
      onEditPortfolio,
      onLoadMore,
      onSearch,
      query,
      searching,
    } = this.props;
    const displayedColumns = this.getDisplayedColumns();
    const displayedMetrics = [PORTFOLIO_METRICS_FIELDS.YEAR_ONE, PORTFOLIO_METRICS_FIELDS.YEAR_THREE, PORTFOLIO_METRICS_FIELDS.YEAR_FIVE];
    const showEmptyCard = benchmarkPortfolios.length === 0 && !query && !searching;

    const getEmptyStateCard = (): JSX.Element => {
      const primaryButton = {
        label: `${this.i18nEmptyStatePath}.PrimaryButtonText`,
        onClick: this.props.onCreatePortfolio,
      };
      return (
        <EmptyStateCard
          imageAlt="Benchmarks placeholder image"
          imagePath={emptyBenchmarksImage}
          messagePath={`${this.i18nEmptyStatePath}.Message`}
          primaryButton={primaryButton}
          titlePath={`${this.i18nEmptyStatePath}.Title`}
        />
      );
    };

    return (
      <>
        {showEmptyCard ? (
          getEmptyStateCard()
        ) : (
          <>
            <div className="models-content-header">
              <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
                {title}
              </Typography>
              <div className="models-content-header-actions custom-benchmarks">
                <SearchInput
                  className="search-input-v2"
                  loading={searching}
                  key="models"
                  placeholder={formatMessage('Models.SearchAllBenchmarkPortfolios')}
                  onChange={onSearch}
                />
                <Button variant="primary" icon="add" onClick={onCreatePortfolio} disabled={this.props.evaluatingPortfolioForComparison}>
                  <FormattedMessage id="ModelsV2.Buttons.NewBenchmark" />
                </Button>
              </div>
            </div>

            <div className="models-content-portfolios">
              {(searching || !initialized) && (
                <div className="models-content-loading-container">
                  <LoadingSpinner />
                </div>
              )}

              <PortfoliosTableV2<BenchmarkPortfolio>
                className={classNames({ 'visually-hidden': searching || !initialized })}
                comparingPortfolioIds={comparingBenchmarkPortfolioIds}
                displayedColumns={displayedColumns}
                displayedMetrics={displayedMetrics}
                hasMore={hasMore}
                onCompare={onCompareSelected}
                onDelete={onDeletePortfolio}
                onEdit={onEditPortfolio}
                onLoadMore={onLoadMore}
                portfolios={benchmarkPortfolios}
                query={query}
                selectedTitle="PortfoliosTable.SelectedTitle.Benchmarks"
                messageIds={{
                  deleteMultiple: 'PortfoliosTable.Buttons.DeleteSelectedBenchmarks',
                  deleteSingle: 'PortfoliosTable.Buttons.DeleteBenchmark',
                  editSingle: 'PortfoliosTable.Buttons.EditBenchmark',
                }}
                performanceSubcomponent={row => {
                  const subComponentProps: PortfolioLinePerformanceProps<BenchmarkPortfolio> = {
                    portfolio: row.original,
                    transformPortfolioToComparisonItem: () => benchmarkToComparisonItem(row.original),
                    displayedMetrics,
                    endpointEntity: 'benchmark-portfolios',
                  };
                  return <PortfolioLinePerformance className="portfolio-line-performance-v2" {...subComponentProps} />;
                }}
                canEditPredicate={() => true}
                canClonePredicate={() => true}
                canDeletePredicate={() => true}
                canAcceptPredicate={() => false}
              />
            </div>
          </>
        )}
      </>
    );
  };

  render() {
    const { benchmarkPortfolios } = this.props;

    return (
      <div ref={this.containerRef} className="models-content">
        {this.renderPortfoliosSection(formatMessage('Models.MyCustomBenchmarks'), benchmarkPortfolios)}
      </div>
    );
  }
}

const AllBenchmarkPortfolios = (props: WrapperProps) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <AllBenchmarkPortfoliosV2 {...props} />;
  }
  return <LegacyBenchmarkPortfolios {...props} />;
};

export default AllBenchmarkPortfolios;
